/**
 * Sprint 944 — Landing variant detection (creative vs business).
 * Sprint 945J — Language switcher persistence helpers.
 *
 * STRATEGIC RULE — creative is the identity, business is the adapted use case.
 *   - Default ALWAYS = "creative"
 *   - ?aud=creative | ?aud=business → explicit override
 *   - Known UTM sources route deliberately
 *   - NO aggressive auto-detection on weak signals (geo, referer, accept-lang, etc.)
 *
 * This module is the single source of truth. Used by:
 *   - hostnameGate (which page to render under the production gate)
 *   - the wizard submit (include `landing_variant` in the POST payload)
 *   - SEO/OG meta selection
 */

const CREATIVE_UTM_SOURCES = new Set([
  "instagram", "ig", "tiktok", "behance", "dribbble", "vimeo",
  "youtube", "pinterest", "vsco", "production_hub",
]);

const BUSINESS_UTM_SOURCES = new Set([
  "linkedin", "li_business", "lawyer_assoc", "medical_assoc",
  "realtor", "consultant_dir", "google_b2b",
]);


/**
 * Pick the variant given a path + search string.
 * @param {string} path     - location.pathname (e.g. "/business")
 * @param {string} search   - location.search   (e.g. "?utm_source=tiktok")
 * @returns {"creative" | "business"}
 */
export function pickLandingVariant(path = "/", search = "") {
  // Path is the strongest signal — explicit route wins.
  if ((path || "").startsWith("/business")) return "business";
  // URL param next.
  let aud = "";
  let utm = "";
  try {
    const p = new URLSearchParams(search || "");
    aud = (p.get("aud") || "").toLowerCase().trim();
    utm = (p.get("utm_source") || "").toLowerCase().trim();
  } catch { /* noop */ }
  if (aud === "creative") return "creative";
  if (aud === "business") return "business";
  if (utm && CREATIVE_UTM_SOURCES.has(utm)) return "creative";
  if (utm && BUSINESS_UTM_SOURCES.has(utm)) return "business";
  // Default — creative landing wins.
  return "creative";
}


/** Convenience for the live browser context. */
export function currentLandingVariant() {
  if (typeof window === "undefined") return "creative";
  return pickLandingVariant(window.location.pathname, window.location.search);
}


/* ─────────────────────────────────────────────────────────────────────────
   Sprint 945J — Language switcher persistence
   ─────────────────────────────────────────────────────────────────────────
   Priority chain (locked, per founder decision May 5, 2026):
     1. ?lang= URL param   →  WINS, AND we mirror it into localStorage
     2. localStorage.splaz_lang
     3. navigator.language (first 2 chars, allowlist en/es/de)
     4. "en" fallback
   On change:
     - Write splaz_lang to localStorage
     - Update URL via history.replaceState to ?lang=<code> while preserving
       all other query params (ref, utm_*, aud, token, …)
   ───────────────────────────────────────────────────────────────────────── */

export const SUPPORTED_LANGS = ["en", "es", "de"];
const LANG_STORAGE_KEY = "splaz_lang";

function _normalizeLang(raw) {
  if (!raw || typeof raw !== "string") return null;
  const code = raw.slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(code) ? code : null;
}

/**
 * Resolve initial language at mount time.
 * Pure given the inputs — easy to unit-test.
 * @param {object}  opts
 * @param {string}  opts.search    - location.search
 * @param {string?} opts.stored    - localStorage value (or null)
 * @param {string?} opts.navLang   - navigator.language (or null)
 * @returns {{ lang: string, source: "url"|"storage"|"navigator"|"default" }}
 */
export function resolveInitialLang({ search = "", stored = null, navLang = null } = {}) {
  // 1. URL param wins.
  try {
    const p = new URLSearchParams(search || "");
    const fromUrl = _normalizeLang(p.get("lang"));
    if (fromUrl) return { lang: fromUrl, source: "url" };
  } catch { /* noop */ }
  // 2. localStorage.
  const fromStorage = _normalizeLang(stored);
  if (fromStorage) return { lang: fromStorage, source: "storage" };
  // 3. navigator.language.
  const fromNav = _normalizeLang(navLang);
  if (fromNav) return { lang: fromNav, source: "navigator" };
  // 4. Default.
  return { lang: "en", source: "default" };
}

/** Browser-side wrapper that wires resolveInitialLang to live globals. */
export function getInitialLang() {
  if (typeof window === "undefined") return "en";
  let stored = null;
  try { stored = window.localStorage?.getItem(LANG_STORAGE_KEY); } catch { /* noop */ }
  const navLang = (typeof navigator !== "undefined" && navigator.language) || null;
  return resolveInitialLang({ search: window.location.search, stored, navLang }).lang;
}

/**
 * Persist a language choice: write to localStorage AND replaceState the URL
 * with ?lang=<code> while preserving all other params. Idempotent + safe.
 */
export function persistLang(lang) {
  const code = _normalizeLang(lang);
  if (!code || typeof window === "undefined") return;
  try { window.localStorage?.setItem(LANG_STORAGE_KEY, code); } catch { /* noop */ }
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("lang") === code) return; // already correct
    url.searchParams.set("lang", code);
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  } catch { /* noop */ }
}
